/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authAPI } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tokens, setTokens] = useState({
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  })

  const logout = useCallback(async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    setUser(null)
    setTokens({ access: null, refresh: null })
  }, [])

  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('access_token')
      const refreshToken = localStorage.getItem('refresh_token')
      
      if (accessToken) {
        try {
          const response = await authAPI.me()
          setUser(response.data.user)
          setTokens({ access: accessToken, refresh: refreshToken })
        } catch (error) {
          // Try refresh token
          if (refreshToken) {
            try {
              const response = await authAPI.refresh(refreshToken)
              localStorage.setItem('access_token', response.data.tokens.access_token)
              localStorage.setItem('refresh_token', response.data.tokens.refresh_token)
              setTokens(response.data.tokens)
              const meResponse = await authAPI.me()
              setUser(meResponse.data.user)
            } catch (refreshError) {
              logout()
            }
          } else {
            logout()
          }
        }
      }
      setLoading(false)
    }
    
    initAuth()
  }, [logout])

  const login = useCallback(async (email, password) => {
    const response = await authAPI.login(email, password)
    const { user, tokens } = response.data
    localStorage.setItem('access_token', tokens.access_token)
    localStorage.setItem('refresh_token', tokens.refresh_token)
    setUser(user)
    setTokens(tokens)
    return response.data
  }, [])

  const register = useCallback(async (data) => {
    const response = await authAPI.register(data)
    const { user, tokens } = response.data
    localStorage.setItem('access_token', tokens.access_token)
    localStorage.setItem('refresh_token', tokens.refresh_token)
    setUser(user)
    setTokens(tokens)
    return response.data
  }, [])

  const refreshToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return false
    
    try {
      const response = await authAPI.refresh(refreshToken)
      localStorage.setItem('access_token', response.data.tokens.access_token)
      localStorage.setItem('refresh_token', response.data.tokens.refresh_token)
      setTokens(response.data.tokens)
      return true
    } catch (error) {
      logout()
      return false
    }
  }, [logout])

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }))
  }, [])

  const value = {
    user,
    tokens,
    loading,
    isAuthenticated: !!user,
    isDemoUser: user?.email === 'demo@sindhlabor.gov.pk',
    login,
    register,
    logout,
    refreshToken,
    updateUser,
    hasRole: (roles) => {
      if (!user) return false
      const rolesArray = Array.isArray(roles) ? roles : [roles]
      return rolesArray.includes(user.role)
    },
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}